import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Page,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  listPlans,
  pendingBillingOperation,
  subscribe,
  type BillingPlan,
  type SubscribeResponse,
} from "../api/billing";
import { getMe } from "../api/me";
import { currentLocale, t } from "../lib/i18n";
import { planLabel } from "../lib/plan";
import type { MeResponse, PlanCode } from "../types";
import { redirectTopLevel } from "../utils/redirect";

/**
 * 価格ではなく、現行の公開仕様と entitlement を説明する表示コピー。
 * 価格は必ず `GET /billing/plans` の値を使い、ここへ重複定義しない。
 */
const PLAN_FEATURES: Record<PlanCode, string[]> = {
  free: [
    "アーカイブ利用枠 月10注文",
    "請求書・領収書 月10枚",
    "検索・単票のプレビューとダウンロード",
    "月間利用枠内のCSV索引簿・一括ZIP",
  ],
  starter: [
    "アーカイブ利用枠 月100注文",
    "請求書・領収書 月100枚",
    "検索・単票のプレビューとダウンロード",
    "月間利用枠内のCSV索引簿・一括ZIP",
  ],
  pro: [
    "アーカイブ利用枠 月1,000注文",
    "請求書・領収書 無制限",
    "CSV索引簿・一括ZIP",
    "検証付き証跡パック（開発中）",
  ],
  business: [
    "アーカイブ・請求書 無制限",
    "CSV索引簿・一括ZIP",
    "検証付き証跡パック（開発中）",
    "優先サポート（予約制）",
  ],
  enterprise: [
    "東京リージョン保管・複数ストアは個別相談",
    "提供条件は相談後に確認",
    "セルフサービス購入は現在受け付けていません",
  ],
};

const ENTERPRISE_CONTACT_FALLBACK =
  "mailto:support@denchobox.app?subject=DenchoBox%20Enterprise%E3%81%AE%E7%9B%B8%E8%AB%87";

const PLAN_CODES: ReadonlySet<string> = new Set<PlanCode>([
  "free",
  "starter",
  "pro",
  "business",
  "enterprise",
]);

/** Shopify Billing の承認を伴うプラン選択画面。購読はボタン操作時にだけ開始する。 */
export function Plans() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmFreeDowngrade, setConfirmFreeDowngrade] = useState(false);

  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const plansQuery = useQuery({ queryKey: ["billing-plans"], queryFn: listPlans });

  const homeLocation = locationWithoutRequired(location.search);
  const params = new URLSearchParams(location.search);
  const returning = params.has("billing_pending") || params.has("billing_operation_id")
    || params.has("charge_id") || pendingBillingOperation() !== null;
  const subscribeMutation = useMutation<SubscribeResponse, Error, PlanCode>({
    mutationFn: async (planCode) => {
      const host = new URLSearchParams(location.search).get("host");
      if (!host) throw new Error(t("埋め込み情報を確認できません。Shopify管理画面からアプリを開き直してください。"));
      const response = await subscribe(planCode, {
        host, locale: currentLocale(), return_path: location.pathname,
      });
      if (response.confirmation_url && !response.billing_operation_id) {
        throw new Error(t("課金操作の保存を確認できませんでした。保存された操作を確認してください。"));
      }
      return response;
    },
    onSuccess: async (response) => {
      if (response.confirmation_url) {
        const search = new URLSearchParams(location.search);
        search.set("billing_pending", "1");
        search.set("billing_operation_id", response.billing_operation_id!);
        navigate({ pathname: location.pathname, search: `?${search}` }, { replace: true });
        // Shopify 管理画面の承認ページは埋め込み iframe 内ではなく最上位へ開く。
        // この URL で店が承認するまで、有料購読は開始されない。
        redirectTopLevel(response.confirmation_url);
        return;
      }

      // Free は Shopify の承認画面を持たず、backend 側で変更が確定済み。
      // 表示中の権限を更新してからホームへ戻す。
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate(homeLocation);
    },
  });

  if (meQuery.isPending || plansQuery.isPending) {
    return (
      <Page title={t("料金プラン")}>
        <Card>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              {t("料金プランを読み込んでいます")}
            </Text>
            <SkeletonBodyText lines={8} />
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (meQuery.isError || plansQuery.isError) {
    const error = meQuery.error ?? plansQuery.error;
    return (
      <Page title={t("料金プラン")}>
        <Banner
          tone="critical"
          title={t("料金プランを読み込めませんでした")}
          action={{
            content: t("再読み込み"),
            onAction: () => {
              if (meQuery.isError) void meQuery.refetch();
              if (plansQuery.isError) void plansQuery.refetch();
            },
          }}
        >
          <Text as="p">{error instanceof Error ? error.message : String(error)}</Text>
        </Banner>
      </Page>
    );
  }

  const me = meQuery.data;
  const plans = plansQuery.data;
  const currentPlanCode: PlanCode =
    me.subscription.active && me.subscription.plan_code
      ? me.subscription.plan_code
      : "free";
  const requiredPlanCode = requiredPlanFromSearch(location.search);
  const requiredPlanName =
    plans.find((plan) => plan.code === requiredPlanCode)?.name ??
    (requiredPlanCode ? planLabel(requiredPlanCode) : null);

  function selectPlan(planCode: PlanCode) {
    if (returning || subscribeMutation.isPending) return;
    // Enterprise の公開 CTA は「相談する」。東京リージョン保管・複数ストアの
    // 提供条件が未確定なまま Shopify Billing を開始させない。
    if (planCode === "enterprise") {
      redirectTopLevel(me.support_call_booking_url ?? ENTERPRISE_CONTACT_FALLBACK);
      return;
    }

    // Free への変更は backend で既存 Shopify 購読の解約を伴うため、即実行しない。
    if (planCode === "free" && currentPlanCode !== "free") {
      setConfirmFreeDowngrade(true);
      return;
    }
    subscribeMutation.mutate(planCode);
  }

  function confirmDowngrade() {
    if (returning || subscribeMutation.isPending) return;
    setConfirmFreeDowngrade(false);
    subscribeMutation.mutate("free");
  }

  return (
    <Page
      title={t("料金プラン")}
      subtitle={t("店舗の利用量に合うプランを選択できます")}
      backAction={{ content: t("ホームへ戻る"), onAction: () => navigate(homeLocation) }}
    >
      <DowngradeModal
        open={confirmFreeDowngrade}
        loading={subscribeMutation.isPending}
        onCancel={() => setConfirmFreeDowngrade(false)}
        onConfirm={confirmDowngrade}
      />

      <Layout>
        {!params.has("billing_pending") && !params.has("billing_operation_id") ? <Layout.Section>
          <Button onClick={() => {
            const search = new URLSearchParams(location.search);
            search.set("billing_pending", "1");
            search.set("billing_operation_id", "current");
            navigate({ search: `?${search}` });
          }}>
            {t("保存された課金操作を確認")}
          </Button>
        </Layout.Section> : null}
        {requiredPlanCode && requiredPlanName ? (
          <Layout.Section>
            <Banner
              tone="warning"
              title={t("{plan}以上のプランが必要です", { plan: requiredPlanName })}
            >
              <Text as="p">
                {t(
                  "この操作を続けるにはプランを選び、Shopifyの承認画面で内容を確認してください。",
                )}
              </Text>
            </Banner>
          </Layout.Section>
        ) : null}

        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="p">
                {t(
                  "有料プランは選択後にShopifyの承認画面へ移動します。承認するまで課金は始まりません。",
                )}
              </Text>
              <Text as="p" tone="subdued">
                {t(
                  "アーカイブ利用枠を超えても、保存・検索・単票のプレビューとダウンロードは止まりません。",
                )}
              </Text>
              <Text as="p" tone="subdued">
                {t(
                  "有料プランは日本円と米ドルの価格を併記しています。実際の請求通貨と金額はShopifyの承認画面で確認してください。",
                )}
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>

        {subscribeMutation.isError ? (
          <Layout.Section>
            <Banner tone="critical" title={t("プラン変更に失敗しました")}>
              <Text as="p">{subscribeMutation.error.message}</Text>
            </Banner>
          </Layout.Section>
        ) : null}

        <Layout.Section>
          {plans.length === 0 ? (
            <Banner tone="warning" title={t("利用できる料金プランがありません")} />
          ) : (
            <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
              {plans.map((plan) => (
                <PlanCard
                  key={plan.code}
                  plan={plan}
                  me={me}
                  isCurrent={plan.code === currentPlanCode}
                  isRequired={plan.code === requiredPlanCode}
                  isPending={subscribeMutation.isPending || returning}
                  isSelectedPending={
                    subscribeMutation.isPending &&
                    subscribeMutation.variables === plan.code
                  }
                  onSelect={() => selectPlan(plan.code)}
                />
              ))}
            </InlineGrid>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function PlanCard({
  plan,
  me,
  isCurrent,
  isRequired,
  isPending,
  isSelectedPending,
  onSelect,
}: {
  plan: BillingPlan;
  me: MeResponse;
  isCurrent: boolean;
  isRequired: boolean;
  isPending: boolean;
  isSelectedPending: boolean;
  onSelect: () => void;
}) {
  const isFree = plan.code === "free";
  const isEnterprise = plan.code === "enterprise";
  const trialDays = effectiveTrialDays(plan, me);
  const price = isFree
    ? t("無料")
    : `¥${plan.price_jpy_monthly.toLocaleString("ja-JP")} / $${plan.price_usd_monthly}`;

  return (
    <Box
      as="section"
      aria-label={plan.name}
      minHeight="100%"
      borderRadius="300"
      outlineColor={isRequired ? "border-info" : undefined}
      outlineStyle={isRequired ? "solid" : undefined}
      outlineWidth={isRequired ? "050" : undefined}
    >
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text as="h2" variant="headingMd">
              {plan.name}
            </Text>
            <InlineStack gap="100">
              {plan.code === "pro" ? <Badge tone="success">{t("おすすめ")}</Badge> : null}
              {isEnterprise ? <Badge>{t("要相談")}</Badge> : null}
              {isRequired ? <Badge tone="info">{t("必要な最小プラン")}</Badge> : null}
              {isCurrent ? <Badge tone="success">{t("利用中")}</Badge> : null}
            </InlineStack>
          </InlineStack>

          <Box paddingBlock="200">
            <Text as="span" variant="heading2xl">
              {price}
            </Text>
            {!isFree ? (
              <Text as="span" tone="subdued">
                {t(" / 月")}
              </Text>
            ) : null}
          </Box>

          {isEnterprise ? (
            <Text as="p" tone="subdued">
              {t("参考価格です。実際の提供条件は相談後に確認します。")}
            </Text>
          ) : null}

          {trialDays > 0 && !isFree && !isEnterprise ? (
            <Text as="p" tone="subdued">
              {me.trial.remaining_days === null
                ? t("初回{days}日間無料（1店舗につき1回）", { days: trialDays })
                : t("無料期間 残り{days}日", { days: trialDays })}
            </Text>
          ) : null}

          <BlockStack gap="100">
            {PLAN_FEATURES[plan.code].map((feature) => (
              <Text key={feature} as="p" variant="bodySm">
                {t("✓ {feature}", { feature: t(feature) })}
              </Text>
            ))}
          </BlockStack>

          <Box paddingBlockStart="200">
            <Button
              fullWidth
              variant={isCurrent || isEnterprise ? "secondary" : "primary"}
              disabled={isCurrent || isPending}
              loading={isSelectedPending}
              onClick={onSelect}
            >
              {planActionLabel(plan, isCurrent, trialDays)}
            </Button>
          </Box>
        </BlockStack>
      </Card>
    </Box>
  );
}

function DowngradeModal({
  open,
  loading,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={t("Freeプランへ変更しますか？")}
      primaryAction={{
        content: t("有料購読を解約してFreeへ変更"),
        destructive: true,
        loading,
        onAction: onConfirm,
      }}
      secondaryActions={[{ content: t("キャンセル"), disabled: loading, onAction: onCancel }]}
    >
      <Modal.Section>
        <Text as="p">
          {t(
            "Freeへ変更すると、現在の有料購読をShopify上で解約します。Freeの利用枠に変わることを確認してから続けてください。",
          )}
        </Text>
      </Modal.Section>
    </Modal>
  );
}

function planActionLabel(
  plan: BillingPlan,
  isCurrent: boolean,
  trialDays: number,
): string {
  if (isCurrent) return t("現在のプラン");
  if (plan.code === "enterprise") return t("Enterpriseについて相談");
  if (plan.code === "free") return t("Freeへ変更");
  if (trialDays > 0) {
    return t("{plan}を{days}日間無料で試す", {
      plan: plan.name,
      days: trialDays,
    });
  }
  return t("{plan}を選ぶ", { plan: plan.name });
}

/** backend の実効 trial 日数と同じ優先順位を、表示だけに反映する。 */
function effectiveTrialDays(plan: BillingPlan, me: MeResponse): number {
  const base = me.trial.remaining_days ?? plan.trial_days;
  const couponDays = me.launch_coupon.available ? me.launch_coupon.trial_days : null;
  return Math.max(0, couponDays === null ? base : Math.max(base, couponDays ?? 0));
}

function requiredPlanFromSearch(search: string): PlanCode | null {
  const value = new URLSearchParams(search).get("required");
  return value && PLAN_CODES.has(value) ? (value as PlanCode) : null;
}

/** 埋め込み用の host / shop / locale は残し、画面専用の required だけ外す。 */
function locationWithoutRequired(search: string): { pathname: string; search: string } {
  const params = new URLSearchParams(search);
  params.delete("required");
  const query = params.toString();
  return { pathname: "/", search: query ? `?${query}` : "" };
}
